import dynamic from "next/dynamic";
import { useMemo } from "react";
import { Step, TooltipRenderProps, CallBackProps } from "react-joyride";
import colors from "tailwindcss/colors";
import Button from "ui/atoms/Button/Button";
import useLocalStorage from "util/useLocaleStorage";

const ReactJoyride = dynamic(() => import("react-joyride"), { ssr: false });

export type GetSteps = (params: {
  showAllSharedGameSteps: boolean;
}) => (Step | false)[];

interface Props {
  getSteps: GetSteps;
  localStorageKey: string;
  disabled?: boolean;
}

function callback(onComplete: () => void) {
  return (props: CallBackProps) => {
    if (props.status === "finished") {
      onComplete();
    }
  };
}

/**
 * Walk through steps specific to Games only
 * After first full game walkthrough, it skips the shared steps for all next games
 */
export default function ReferenceTrack({
  getSteps,
  localStorageKey,
  disabled,
}: Props) {
  // keep track across all games if the user has completed one reference track
  // so we can skip the shared steps for all next games and only show specific steps that are diff for the current game
  const [fullGameRefTrackCompleted, setFullGameRefTrackCompleted] =
    useLocalStorage(`one-full-game-ref-track-completed`, false);
  const [refTrackCompleted, setRefTrackCompleted] = useLocalStorage(
    `${localStorageKey}-ref-track-completed`,
    false
  );

  const steps: Step[] = useMemo(
    () =>
      getSteps({
        showAllSharedGameSteps: !fullGameRefTrackCompleted,
      }).filter(Boolean) as Step[],
    [fullGameRefTrackCompleted, getSteps]
  );

  const onComplete = () => {
    setFullGameRefTrackCompleted(true);
    setRefTrackCompleted(true);
  };

  if (refTrackCompleted) {
    return null;
  }

  return (
    <ReactJoyride
      steps={steps}
      callback={callback(onComplete)}
      run={!disabled}
      continuous
      disableOverlay
      disableScrolling
      showProgress
      tooltipComponent={ToolTip}
      styles={styles}
    />
  );
}

const styles = {
  beaconInner: {
    backgroundColor: "white",
  },

  beaconOuter: {
    backgroundColor: colors.purple[500],
    border: `2px solid ${colors.purple[500]}}`,
  },
};

function ToolTip({
  continuous,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  tooltipProps,
  isLastStep,
}: TooltipRenderProps) {
  return (
    <div
      {...tooltipProps}
      className="bg-black border-2 border-white p-4 rounded text-white max-w-[300px]"
    >
      {step.title && (
        <div className={"font-brik text-2xl mb-2 leading-none"}>
          {step.title}
        </div>
      )}

      <div className={"mb-4"}>{step.content}</div>

      <div className={"flex justify-between items-center"}>
        {index > 0 ? (
          <Button {...backProps} theme="default">
            Back
          </Button>
        ) : (
          <div />
        )}

        {continuous ? (
          <Button {...primaryProps} theme="default">
            {isLastStep ? "Done" : "Next"}
          </Button>
        ) : (
          <div />
        )}

        {/* {!continuous && (
          <Button {...closeProps} theme="default">
            Close
          </Button>
        )} */}
      </div>
    </div>
  );
}
